import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import { useMemo } from "react"
import { NexPageSize, nexPageSizes } from "../../../components/Table/pageable-table/NexPageSize"
import { useNexusNavigate } from "../../../hooks/useNexusNavigate"
import { useNexusSensorIdOptional, useSensorInstallSearchFiltersWithDefaults } from "../../../hooks/useNexusParams"
import { Order } from "../../../models/SortOrder"
import { SensorInstallInfo } from "../../../models/sensors/SensorInstallInfo"
import {
    SensorInstallOrder,
    sensorInstallDefaultOrder,
    sensorInstallDefaultOrderBy,
} from "./SensorInstallSearchFilters"
import { SensorInstallToolbar } from "./SensorInstallToolbar"

interface SensorInstallTableProps {
    columns: GridColDef<SensorInstallInfo>[]
    rows?: SensorInstallInfo[]
    count?: number
    isLoading: boolean
}

export const SensorInstallTable = (props: SensorInstallTableProps) => {
    const { columns, rows = [], count = -1, isLoading } = props
    const nexNavigate = useNexusNavigate()
    const sensorId = useNexusSensorIdOptional()

    const currentFiltersWithDefaults = useSensorInstallSearchFiltersWithDefaults()

    // We must memoize this sort model, or it causes unexpected sideeffects with the pagination
    const sortModel = useMemo(
        () => [{ field: currentFiltersWithDefaults.ordering, sort: currentFiltersWithDefaults.order }],
        [currentFiltersWithDefaults.ordering, currentFiltersWithDefaults.order],
    )

    const paginationModel = useMemo(
        () => ({
            page: currentFiltersWithDefaults.page,
            pageSize: currentFiltersWithDefaults.page_size,
        }),
        [currentFiltersWithDefaults.page, currentFiltersWithDefaults.page_size],
    )

    // Handler Functions
    const handlePaginationModelChange = (model: GridPaginationModel) => {
        nexNavigate.searchSensorInstalls(
            {
                ...currentFiltersWithDefaults,
                page_size: model.pageSize as NexPageSize,
                page: model.page,
            },
            sensorId,
        )
    }

    const handleSortModelChange = (model: GridSortModel) => {
        let sortItem = model[0]
        if (model.length === 0) {
            sortItem = { field: sensorInstallDefaultOrderBy, sort: sensorInstallDefaultOrder }
        }
        nexNavigate.searchSensorInstalls(
            {
                ...currentFiltersWithDefaults,
                page: 0, // reset the table to the beginning on sort change
                order: sortItem.sort as Order,
                ordering: sortItem.field as SensorInstallOrder,
            },
            sensorId,
        )
    }

    return (
        <DataGrid
            columns={columns}
            rows={rows}
            rowCount={count}
            loading={isLoading}
            pageSizeOptions={nexPageSizes}
            slots={{ toolbar: SensorInstallToolbar }}
            showToolbar={true}
            disableRowSelectionOnClick={true}
            disableColumnFilter={true}
            filterMode="server"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
        />
    )
}
